import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || categories[0].slug;
  const [isTransitioning, setIsTransitioning] = useState(false);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeTab) || categories[0],
    [activeTab]
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeTab),
    [activeTab]
  );

  const handleTabChange = (slug: string) => {
    if (slug === activeTab) return;
    setIsTransitioning(true);
    setSearchParams({ tab: slug });
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://shaoshangliang.cn/' },
          { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://shaoshangliang.cn/categories' },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `久久爱 - ${activeCategory.name}`,
        description: activeCategory.description,
        url: `https://shaoshangliang.cn/categories?tab=${activeTab}`,
      },
    ],
  };

  return (
    <div className="pt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '分类浏览' }]} />

        <div className="mt-8 mb-10">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
            分类浏览
          </h1>
          <p className="mt-3 text-foreground-500 text-sm">
            选择你感兴趣的分类，探索专业情感知识与服务
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-10 px-1 py-1 bg-background-100 rounded-full p-1.5 inline-flex">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleTabChange(cat.slug)}
              className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeTab === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="mb-8 p-6 rounded-xl bg-background-100 border border-background-200/70">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
              <i className={`${activeCategory.icon} text-xl text-primary-500`}></i>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground-900">{activeCategory.name}</h2>
              <span className="text-xs text-foreground-500">{activeCategory.count} 篇文章</span>
            </div>
          </div>
          <p className="text-sm text-foreground-600 mt-3">{activeCategory.description}</p>
        </div>

        <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
          {filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-article-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer flex flex-col"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`${article.title} - 久久爱${article.category}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-3">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-4 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400 pt-4 border-t border-background-200/70 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <img
                          src={article.authorAvatar}
                          alt={article.author}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                        <span>{article.author}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span>{article.publishDate}</span>
                        <span>{article.readTime} 分钟</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </div>
  );
}